import { AFTER_ONBOARDING_SURVEY_EVENT, currencyMap } from './constants';
import type { NuxtThirdPartyPlugin } from '@/plugins/3rd-party/types';
import type { UserLeap } from '@/plugins/3rd-party/userleap/types.d';
import { pluralize } from '@/plugins/filters';
import type {
  CompanySeriesStage,
  CompanyStage,
  Department,
  Industry,
  IntRange,
  ProductType,
  SalesRole,
  JobsVacancySubmission
} from '@/types/graphql';
import { Currency, JobsVacancyEmployment } from '@/types/graphql';
import type { CurrencyData } from '~/components/partials/jobs/types';

type MultiselectFieldType =
  | (Department | Industry | CompanySeriesStage | ProductType | CompanyStage | SalesRole | string)[]
  | null
  | undefined;

export const isSingleFieldFilled = (value: string | number | undefined | null | boolean): boolean => {
  if (typeof value === 'boolean') return true;

  return Boolean(value);
};

export const isMultiselectFieldHasSelectedOption = (value: MultiselectFieldType): boolean => {
  if (!value) return false;

  return Boolean(value.length);
};

export const isMultiselectFieldHasSelectedOptionOrOtherOptionFilled = (
  mainValue: MultiselectFieldType,
  otherValue: string | undefined | null,
): boolean => {
  return isMultiselectFieldHasSelectedOption(mainValue) || isSingleFieldFilled(otherValue);
};

const getDaysInCurrentMonth = () => {
  const currentDate = new Date();

  return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
};

export const getDaysSinceInWords = (daysSince?: number | null) => {
  if (!daysSince) return undefined;

  const daysInCurrentMonth = getDaysInCurrentMonth();

  if (daysSince === 1) return 'Today';
  if (daysSince > 1 && daysSince <= daysInCurrentMonth) return `${daysSince} ${pluralize(daysSince, 'day', 'days')}`;

  const monthsSince = Math.floor(daysSince / 30);
  return `${monthsSince} ${pluralize(monthsSince, 'month', 'months')}`;
};

// refactor loading of plugins, should have method which will resolve Promise on load
const waitForUserleap = (userleapPlugin: UserLeap, onReady: (arg?: string) => void) => {
  setTimeout(() => {
    if (!userleapPlugin.track) {
      waitForUserleap(userleapPlugin, onReady);
      return;
    }

    onReady();
  }, 100);
};

const loadUserleap = (userleapPlugin: UserLeap) => {
  return new Promise(resolve => {
    waitForUserleap(userleapPlugin, resolve);
  });
};

export const runSurvey = async (surveyId: string, plugins: NuxtThirdPartyPlugin) => {
  if (!plugins.userleap.loaded) {
    await plugins.loadImmediately(['userleap']);
  }

  await loadUserleap(plugins.userleap);
  return await plugins.userleap.track(surveyId);
};

export const runSurveyAfterJobsOnboarding = async (plugins: NuxtThirdPartyPlugin) => {
  const eventName = sessionStorage.getItem(AFTER_ONBOARDING_SURVEY_EVENT);

  if (!eventName) return;

  sessionStorage.removeItem(AFTER_ONBOARDING_SURVEY_EVENT);

  await runSurvey(eventName, plugins);
};

export const formatCaptions = (autoCaptions: Array<string> | null): Array<string> => {
  if (!autoCaptions) return [];
  const replaceFunc = (c: string) => c.replace(/"|\\n/g, '');
  return autoCaptions.filter(c => replaceFunc(c)).map(c => `✅ ${replaceFunc(c)}`);
};

export const isFlexEmploymentType = (employmentType: JobsVacancyEmployment) => {
  const { Contract, SideHustle } = JobsVacancyEmployment;

  return [Contract, SideHustle].includes(employmentType);
};

export const getCurrencyData = (currency?: Currency | null): CurrencyData => {
  const defaultCurrency = Currency.Usd;
  return currencyMap[currency || defaultCurrency];
};

export const getVacancyRangeDescription = ({ range }: {
  range?: IntRange | null;
}) => {
  const formatter = Intl.NumberFormat('en', { notation: 'compact' });

  if (!range) {
    // $sentryUtils.error('No range found in submission');

    return null;
  }

  const { min, max } = range;

  const minValue = formatter.format(min);
  const maxValue = formatter.format(max);

  return `$${minValue} - $${maxValue}`;
};

export const getVacancyLocation = ({
  vacancySubmission,
  employmentType,
}: {
  vacancySubmission: JobsVacancySubmission;
  employmentType: JobsVacancyEmployment;
}) => {
  const countries = vacancySubmission?.countries?.map(country => country.name).join(', ');
  const cities = vacancySubmission?.cities?.map(city => `${city.name}, ${city.stateCode}`).join(', ');
  const remoteText = 'Remote';

  if (isFlexEmploymentType(employmentType) || vacancySubmission?.openToRemoteWork) {
    return countries ? `${countries} (${remoteText})` : remoteText;
  }

  return cities ? `${cities} (${remoteText})` : remoteText;
};
